import { matchPath, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Divider,
  Flex,
  H5,
  Loading,
  Notification,
  Small,
} from "orcs-design-system";

import PATHS, { url } from "src/consts/urlPaths";
import InboxList from "src/inbox/InboxList";
import { arrivedFromInterstitial } from "src/inbox/inboxTransition";
import {
  filtersFromSearch,
  inboxFilterCaption,
  inboxFilterLabels,
} from "src/inbox/kinds";
import { INBOX_PAGE_SIZE, useInboxItems } from "src/inbox/useInboxItems";
import { useInboxWaitingCount } from "src/inbox/useInboxWaitingCount";

/** Highlight the open item. `/inbox` with no id shows the landing, so none. */
export const selectedInboxKey = (pathname: string): string | null =>
  matchPath<{ itemId?: string }>(pathname, {
    path: PATHS.WORKFORCE_INBOX,
  })?.params.itemId ?? null;

const InboxContent = () => {
  const history = useHistory();
  const { pathname, search, state } = useLocation();
  const { category, routeId } = filtersFromSearch(search);
  const {
    items,
    total,
    routes,
    loading,
    error,
    hasMore,
    loadingMore,
    loadMore,
    categoryPending,
  } = useInboxItems(true, { category, routeId, pageSize: INBOX_PAGE_SIZE });
  const waitingCount = useInboxWaitingCount(true);
  const selectedKey = selectedInboxKey(pathname);
  const arrivedKey = arrivedFromInterstitial(state) ? selectedKey : null;
  const showLoading = loading && items.length === 0 && !categoryPending;
  const showError = !showLoading && Boolean(error) && items.length === 0;
  const { categoryLabel, routeLabel } = inboxFilterLabels({
    category,
    routeId,
    routes,
  });
  const caption = inboxFilterCaption({
    categoryLabel,
    routeLabel,
    total,
    waiting: waitingCount ?? total,
  });

  return (
    // Block-level, not a flex container: the panel's close button is floated
    // right, and a flex BFC will not overlap it — the whole inbox column
    // would stay narrowed for the full panel height. Same as Dashboards.
    // Absolute fill is inside this box, so it is full width; the float only
    // overlaps the in-flow title row.
    <Box height="100%" position="relative">
      <Flex
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        flexDirection="column"
        minHeight={0}
        overflow="hidden"
      >
        <H5 fontWeight="bold">Inbox</H5>
        <Box my="r">
          <Divider light />
        </Box>
        {caption && !showLoading ? (
          <Box mb="s">
            <Small color="subText">{caption}</Small>
          </Box>
        ) : null}
        {showLoading ? <Loading large primary centered /> : null}
        {showError ? (
          <Notification variant="error" closable={false}>
            The inbox could not be loaded.
          </Notification>
        ) : null}
        {!showLoading && !showError ? (
          <Box flex="1 1 auto" minHeight={0} overflow="hidden">
            <InboxList
              items={items}
              selectedKey={selectedKey}
              arrivedKey={arrivedKey}
              otherCount={
                category === "EVERYTHING" && !routeId
                  ? 0
                  : Math.max(0, (waitingCount ?? 0) - total)
              }
              hasMore={hasMore}
              loadingMore={loadingMore}
              onSelect={(key) => {
                history.push(
                  url(PATHS.WORKFORCE_INBOX, { itemId: key }) + search
                );
              }}
              onLoadMore={loadMore}
            />
          </Box>
        ) : null}
      </Flex>
    </Box>
  );
};

export default InboxContent;
